import { useState } from "react";
import { PageLayout } from "@/components/PageLayout";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { CalculatorSuperCalcCTA } from "@/components/CalculatorSuperCalcCTA";
import { calculatorCTAs } from "@/data/calculatorCTAs";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { toast } from "@/hooks/use-toast";
import { Math as MathFormula } from "@/components/Math";
import { CopyResultButton } from "@/components/CopyResultButton";

export default function StandardDeviationCalculator() {
  const [data, setData] = useState("");
  const [result, setResult] = useState<{
    mean: number;
    variance: number;
    stdDev: number;
    count: number;
    sum: number;
  } | null>(null);

  const calculate = () => {
    const numbers = data
      .split(/[\s,]+/)
      .map(n => parseFloat(n.trim()))
      .filter(n => !isNaN(n));

    if (numbers.length === 0) {
      toast({
        title: "Invalid Input",
        description: "Please enter valid numbers separated by spaces or commas.",
        variant: "destructive",
      });
      return;
    }

    const sum = numbers.reduce((acc, n) => acc + n, 0);
    const mean = sum / numbers.length;

    const squaredDiffs = numbers.map(n => Math.pow(n - mean, 2));
    const variance = squaredDiffs.reduce((acc, d) => acc + d, 0) / numbers.length;
    const stdDev = Math.sqrt(variance);

    setResult({
      mean,
      variance,
      stdDev,
      count: numbers.length,
      sum,
    });

    toast({
      title: "Calculation Complete",
      description: `Standard deviation: ${stdDev.toFixed(4)}`,
    });
  };

  const reset = () => {
    setData("");
    setResult(null);
  };

  return (
    <PageLayout 
      calculatorName="Standard Deviation Calculator" 
      calculatorPath="/calculator/standard-deviation"
      category="math"
      subcategory="Statistics"
    >
      <div className="max-w-2xl mx-auto">
        <Card>
          <CardHeader>
            <CardTitle>Standard Deviation & Variance Calculator</CardTitle>
            <CardDescription>
              Measure the spread of data from the mean
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="space-y-2">
              <Label htmlFor="data">Data Set (separate with spaces or commas)</Label>
              <Textarea
                id="data"
                value={data}
                onChange={(e) => setData(e.target.value)}
                placeholder="e.g., 5, 10, 15, 20, 25"
                rows={4}
              />
            </div>

            <div className="flex gap-4">
              <Button onClick={calculate} className="flex-1">Calculate</Button>
              <Button onClick={reset} variant="outline" className="flex-1">Reset</Button>
            </div>

            {result && (
              <>
                <div className="space-y-4">
                  <Card className="bg-muted/50">
                    <CardHeader>
                      <CardTitle className="text-lg">Results</CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-3">
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <p className="text-sm text-muted-foreground">Count</p>
                          <p className="text-2xl font-bold">{result.count}</p>
                        </div>
                        <div>
                          <p className="text-sm text-muted-foreground">Sum</p>
                          <p className="text-2xl font-bold">{result.sum.toFixed(2)}</p>
                        </div>
                      </div>
                      <hr className="border-muted" />
                      <div>
                        <p className="text-sm text-muted-foreground">Mean (μ)</p>
                        <p className="text-2xl font-bold">{result.mean.toFixed(4)}</p>
                      </div>
                      <div>
                        <p className="text-sm text-muted-foreground">Variance (σ²)</p>
                        <p className="text-2xl font-bold">{result.variance.toFixed(4)}</p>
                      </div>
                      <div>
                        <p className="text-sm text-muted-foreground">Standard Deviation (σ)</p>
                        <div className="flex items-start justify-between gap-4">
                          <p className="text-3xl font-bold text-primary break-all overflow-wrap-anywhere flex-1">{result.stdDev.toFixed(4)}</p>
                          <CopyResultButton value={result.stdDev.toFixed(4)} />
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </div>

                <CalculatorSuperCalcCTA config={calculatorCTAs["standard-deviation"]} trigger={result?.stdDev} />
              </>
            )}

            <Card className="bg-muted/50">
              <CardHeader>
                <CardTitle className="text-sm">Formulas</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <p className="text-sm font-semibold mb-2">Variance (σ²)</p>
                  <MathFormula block>{String.raw`\sigma^2 = \frac{1}{n}\sum_{i=1}^{n}(x_i - \mu)^2`}</MathFormula>
                </div>
                <div>
                  <p className="text-sm font-semibold mb-2">Standard Deviation (σ)</p>
                  <MathFormula block>{String.raw`\sigma = \sqrt{\frac{1}{n}\sum_{i=1}^{n}(x_i - \mu)^2}`}</MathFormula>
                </div>
              </CardContent>
            </Card>
          </CardContent>
        </Card>
      </div>
    </PageLayout>
  );
}
